import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import CookieConsent from "@/components/CookieConsent";

// Everything except the public Landing page is lazy-loaded — it isn't needed for first paint.
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Assessment = lazy(() => import("@/pages/Assessment"));
const Portal = lazy(() => import("@/pages/Portal"));
const Register = lazy(() => import("@/pages/Register"));
const RegistrationApproved = lazy(() => import("@/pages/RegistrationApproved"));
const QuickBooksLaunch = lazy(() => import("@/pages/QuickBooksLaunch"));
const Privacy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const Terms = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const Content = lazy(() => import("@/pages/Content"));
const ContentDetail = lazy(() => import("@/pages/ContentDetail"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-upland-offwhite font-sans text-upland-grey">Loading…</div>
);

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <PageLoader />;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/booking" element={<Landing />} />
            <Route path="/assessment" element={<Assessment />} />
            <Route path="/register" element={<Register />} />
            <Route path="/registration-approved" element={<RegistrationApproved />} />
            <Route path="/quickbooks" element={<QuickBooksLaunch />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/legal/privacy" element={<Privacy />} />
            <Route path="/legal/terms" element={<Terms />} />
            <Route path="/content" element={<Content />} />
            <Route path="/content/:slug" element={<ContentDetail />} />
            <Route path="/portal" element={<Portal />} />
            <Route path="/portal/login" element={<Portal />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/settings" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/team" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/users" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/billing" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/storage" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/calendar" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" />
      <CookieConsent />
    </AuthProvider>
  );
}

export default App;
